<template>
  <v-expansion-panel>
    <div
      draggable="true"
      @dragstart.self="$emit('drag-start', $event, jobStep.step)"
      @dragend.stop="$emit('drag-end')"
      @drop="$emit('drop', $event, jobStep)"
      @dragover.prevent
      @dragenter.prevent
    >
      <v-expansion-panel-header>
        <span class="pl-4 text-caption font-italic">
          <b>{{ `Step ${index + 1}` }}</b>
          {{ headerText }}
          <span class="patch" v-if="missingCount > 0">{{ missingCount }}</span>
        </span>
        <v-spacer />
        <v-btn
          @click.stop="$emit('remove', index)"
          :disabled="stepCount === 1"
          class="remove-btn"
          small
          text
        >
          <v-icon class="mr-2">delete_outline</v-icon>
          Remove
        </v-btn>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-row>
          <v-col cols="8">
            <label :for="fieldId('jobStep')"
              >Job Step<span class="red--text ml-2">*</span></label
            >
            <v-text-field
              :id="fieldId('jobStep')"
              v-model="jobStep.name"
              :placeholder="`Job Step ${jobStep.step}`"
              counter="255"
              maxlength="255"
              :rules="rules"
              @blur="emitChange"
              outlined
              required
              dense
            />
          </v-col>
          <v-col cols="4">
            <label :for="fieldId('Employees')"
              >Employees <span class="red--text ml-2">*</span></label
            >
            <v-select
              :id="fieldId('Employees')"
              v-model="jobStep.employeeIds"
              :items="employeeItems"
              attach
              outlined
              @blur="emitChange"
              multiple
              style="min-height:67px!important"
              chips
              dense
            >
              <template slot="selection" slot-scope="data">
                <span class="pr-2 selected-barrier">
                  {{ data.item.text }}
                  <v-icon
                    small
                    @click="onRemoveItem(jobStep.employeeIds, data.item.value)"
                    >close</v-icon
                  ></span
                >
              </template>
              <template slot="item" slot-scope="data">
                <span class="pb-1 pt-2">{{ data.item.text }}</span>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row
          v-for="(jobStepHazard, j) in jobStep.jobStepHazards"
          :key="jobStepHazard.id"
          no-gutters
        >
          <v-col class="flex-grow-1 flex-shrink-0">
            <v-row>
              <v-col cols="4" class="py-0">
                <label :for="fieldId('hazard', j)"
                  >{{ `Hazard ${j + 1}`
                  }}<span class="red--text ml-2">*</span></label
                >
                <v-select
                  :id="fieldId('hazard', j)"
                  :value="jobStepHazard.hazardId"
                  :items="hazardItems"
                  :rules="rules"
                  attach
                  @input="updateHazard(jobStepHazard, $event)"
                  outlined
                  required
                  dense
                >
                  <template slot="selection" slot-scope="data">
                    <div>{{ data.item.text }}</div>
                  </template>
                  <template slot="item" slot-scope="data">
                    <span class="pb-1 pt-2">{{ data.item.text }}</span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="4" class="py-0">
                <label :for="fieldId('source', j)">Source</label>
                <v-text-field
                  :id="fieldId('source', j)"
                  counter="255"
                  maxlength="255"
                  @blur="emitChange"
                  v-model="jobStepHazard.source"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="4" class="py-0">
                <label :for="fieldId('barriers', j)"
                  >Barriers<span class="red--text ml-2">*</span></label
                >
                <v-combobox
                  v-model="jobStepHazard.barriers"
                  :items="barrierItems(jobStepHazard.hazardId)"
                  :id="fieldId('barriers', j)"
                  attach
                  outlined
                  multiple
                  @blur="emitChange"
                  dense
                >
                  <template v-slot:selection="{ item }">
                    <span class="pr-2 selected-barrier"
                      >{{ item }}
                      <v-icon
                        small
                        @click="onRemoveItem(jobStepHazard.barriers, item)"
                        >close</v-icon
                      >
                    </span>
                  </template>
                  <template v-slot:item="{ item }">
                    <span class="pr-1 ma-1">{{ item }} </span>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
          </v-col>
          <v-col class="flex-grow-0 flex-shrink-1 pt-4">
            <v-btn @click="removeHazard(j)" icon class="ml-2">
              <v-icon>delete_outline</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn class="primary" @click.stop="newHazard">Assign Hazard</v-btn>
          </v-col>
        </v-row>
      </v-expansion-panel-content>
    </div>
  </v-expansion-panel>
</template>
<script>
import utils from "@/shared/Utils";
import { countRequired } from "@/utilities/job-steps";

// One job step. Keeping each step in its own component means a keystroke in
// one step only re-renders that step; previously the whole job-steps section
// (every panel, select and combobox for every step) re-rendered on each key,
// which on a 30-step risk assessment made typing feel frozen (SUPP-9155).
export default {
  name: "JobStep",

  props: {
    jobStep: { type: Object, required: true },
    index: { type: Number, required: true },
    stepCount: { type: Number, required: true },
    selectedWorkType: Object,
    employeeItems: { type: Array, default: () => [] },
    hazardItems: { type: Array, default: () => [] },
    barrierNamesByHazardId: { type: Object, default: () => ({}) },
    rules: { type: Array, default: () => [] }
  },

  computed: {
    headerText() {
      if (this.selectedWorkType) return this.jobStep.name;
      return this.jobStep.name || "New Job Step - Click to expand";
    },

    missingCount() {
      const { required, hasData } = countRequired(this.jobStep);
      return required - hasData;
    }
  },

  methods: {
    // Ids were previously only unique within a step ("hazard0" in every
    // step); include the step index so labels point at the right input.
    fieldId(prefix, j) {
      return j === undefined
        ? `${prefix}${this.index}`
        : `${prefix}${this.index}-${j}`;
    },

    emitChange() {
      this.$emit("change");
    },

    barrierItems(hazardId) {
      return this.barrierNamesByHazardId[hazardId] || [];
    },

    updateHazard(jobStepHazard, hazardId) {
      if (jobStepHazard.hazardId !== hazardId) {
        jobStepHazard.barriers = null;
        jobStepHazard.hazardId = hazardId;
        this.emitChange();
      }
    },

    newHazard() {
      const hazards = this.jobStep.jobStepHazards;
      hazards.push({
        id: utils.getUUID(),
        source: null,
        position: hazards.length + 1,
        hazardId: null,
        barriers: []
      });
      this.emitChange();
    },

    removeHazard(index) {
      const hazards = this.jobStep.jobStepHazards;
      hazards.splice(index, 1);
      for (let i = 0; i < hazards.length; i++) {
        hazards[i].position = i + 1;
      }
      this.emitChange();
    },

    onRemoveItem(list, item) {
      const i = list.indexOf(item);
      list.splice(i, 1);
      this.emitChange();
    }
  }
};
</script>
<style scoped>
label {
  color: var(--v-primary-base) !important;
  margin-bottom: 5px !important;
}

.patch {
  padding-top: 1px;
  text-align: center;
  float: left;
  width: 22px !important;
  height: 22px;
  border-radius: 11px;
  background-color: var(--v-error-base);
  color: white;
  font-size: 12px;
  margin-right: 5px;
}

.selected-barrier {
  border-radius: 5px;
  padding: 4px;
  margin: 4px;
  background-color: #d8d8d8;
}
</style>
